import React, { Dispatch, SetStateAction } from 'react';
import { Box, Checkbox, FormControlLabel, Stack } from '@mui/material';
import VirtualizeGrouped from '../../../Components/CustomUIElements/VirtualizedGroupedAutocomplete';
import { sharedClasses } from '../../../Components/CustomUIElements/sharedClasses';
import HtmlEditor from '../../../Components/Utilities/HtmlEditor';
import { classes } from '../styles';
import NotificationOptionSwitch from '../../../Application/Modals/Components/NotificationOptionsSwitch';
import SmsTemplateComponent from '../../../Components/CustomUIElements/SmsTemplateComponent';
import { ISMSTemplate } from '../../types';
import { StyledSnackbarProps } from '../../../Components/CustomUIElements/StyledSnackbar';

interface InviteToTimeslotStep2Props {
  notification: string;
  setNotification: Dispatch<SetStateAction<string>>;
  selectedTemplate: string | null;
  setSelectedTemplate: (template: string | null) => void;
  emailTemplates: string[];
  loadingEmailTemplates: boolean;
  handleSelectTemplate: (template: string) => void;
  errors: Record<string, string>;
  setErrors: (errors: Record<string, string>) => void;
  body: string;
  setBody: (body: string) => void;
  sendableBody: string;
  setSendableBody: (body: string) => void;
  smsMessage: string;
  setSmsMessage: Dispatch<SetStateAction<string>>;
  smsTemplate: ISMSTemplate | null;
  setSmsTemplate: Dispatch<SetStateAction<ISMSTemplate | null>>;
  excludeApplicants: boolean;
  setExcludeApplicants: (exclude: boolean) => void;
  placeholders: string;
  templateRef: React.RefObject<HTMLDivElement>;
  bodyRef: React.RefObject<HTMLDivElement>;
  smsTemplatePermissions: boolean;
  setSnackbar: StyledSnackbarProps['setSnackbarState'];
}

export default function InviteToTimeslotStep2({
  notification,
  setNotification,
  selectedTemplate,
  setSelectedTemplate,
  emailTemplates,
  loadingEmailTemplates,
  handleSelectTemplate,
  errors,
  setErrors,
  body,
  setBody,
  sendableBody,
  setSendableBody,
  smsMessage,
  setSmsMessage,
  smsTemplate,
  setSmsTemplate,
  excludeApplicants,
  setExcludeApplicants,
  placeholders,
  templateRef,
  bodyRef,
  smsTemplatePermissions,
  setSnackbar
}: InviteToTimeslotStep2Props) {
  return (
    <Box>
      <NotificationOptionSwitch setNotification={setNotification} notification={notification} />
      {notification === 'email' ? (
        <Stack sx={{ paddingTop: 4, rowGap: 3 }}>
          <VirtualizeGrouped
            value={selectedTemplate}
            options={emailTemplates || []}
            onChange={(value) => {
              value ? handleSelectTemplate(value) : setBody('');
            }}
            styles={{ ...sharedClasses.formAutocomplete, width: '600px' }}
            label="Template"
            loading={loadingEmailTemplates}
            loadingText="Loading template list..."
            innerRef={templateRef}
            error={errors.template}
            errorStyles={{ ...sharedClasses.errorBox, top: '-24px', right: '0px' }}
          />
          <FormControlLabel
            control={
              <Checkbox
                sx={classes.formCheckbox}
                onChange={(e) => setExcludeApplicants(e.target.checked)}
                value={excludeApplicants}
              />
            }
            label="Exclude applicants who have received this email template"
            sx={classes.checkboxLabel}
          />
          <Box ref={bodyRef}>
            <Stack sx={{ ...classes.formLabel, flexDirection: 'row', columnGap: '2px' }}>
              Body <Stack sx={{ color: '#E37D7A' }}>*</Stack>
            </Stack>
            <HtmlEditor body={body} onChange={setSendableBody} />
            <Stack sx={sharedClasses.errorBox}>{errors.body}</Stack>
            <Box sx={classes.placeholderText}>Available placeholders are: {placeholders}</Box>
          </Box>
        </Stack>
      ) : (
        <SmsTemplateComponent
          smsTemplatePermissions={smsTemplatePermissions}
          setSnackbar={setSnackbar}
          smsMessage={smsMessage}
          setSmsMessage={setSmsMessage}
          smsTemplate={smsTemplate}
          setSmsTemplate={setSmsTemplate}
          smsMessageError={errors.sms}
        />
      )}
    </Box>
  );
}
